import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProvinceEventFeed } from '../hooks/useProvinceEventFeed';
import { advanceProvinceEventCursor } from '../api/provinceEvents';
import { formatGameDate } from '../utils/gameTime';
import { formatEnumLabel } from '../utils/format';
import { SEVERITY_STYLE } from '../utils/provinceEventSeverity';
import { COLORS } from '../constants/theme';
import { Spinner } from '../components/Spinner';
import type { ProvinceEventDto } from '../types/provinceEvents';

const PAGE_SIZE = 50;

export function ProvinceEventFeed() {
  const { worldId, provinceId } = useAuth();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const markedSeenForRef = useRef<number | null>(null);

  const { items, isLoading, isLoadingMore, error, hasMore, loadMore } =
    useProvinceEventFeed(worldId, provinceId, PAGE_SIZE);

  useEffect(() => {
    h1Ref.current?.focus();
  }, []);

  // Component 8/Decision 7: mark-seen fires exactly once per real newest-event change (keyed on
  // the newest loaded event's eventSeq, a stable primitive -- not array identity, which would
  // re-fire on every re-render/page load, RecentNews.tsx's known bug).
  useEffect(() => {
    if (!worldId || !provinceId || items.length === 0) return;
    const newestEventSeq = items[0].eventSeq;
    if (markedSeenForRef.current === newestEventSeq) return;
    markedSeenForRef.current = newestEventSeq;
    void advanceProvinceEventCursor(worldId, provinceId, newestEventSeq);
  }, [worldId, provinceId, items]);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 760 }}>
      <h1
        ref={h1Ref}
        tabIndex={-1}
        style={{ color: COLORS.text.primary, fontSize: 22, fontWeight: 700, marginBottom: 4, outline: 'none' }}
      >
        Incoming Events
      </h1>
      <p style={{ color: COLORS.text.tertiary, fontSize: 14, marginBottom: 8 }}>
        Hostile activity targeting your province
      </p>
      <p style={{ color: COLORS.text.tertiary, fontSize: 12, marginBottom: 24, fontStyle: 'italic' }}>
        Attackers under an Anonymity spell, or thieves who evaded detection, are not named.
      </p>

      {isLoading && items.length === 0 && <Spinner />}

      {error && (
        <div style={{ color: COLORS.errorText, fontSize: 14, marginBottom: 16 }}>{error}</div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div style={{ color: COLORS.text.tertiary, fontSize: 14 }}>
          No incoming activity yet.
        </div>
      )}

      <div
        aria-busy={isLoading || isLoadingMore}
        style={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {items.map((event) => (
          <ProvinceEventRow key={event.eventSeq} event={event} />
        ))}
      </div>

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={isLoadingMore}
          style={{
            marginTop: 16,
            padding: '8px 20px',
            backgroundColor: COLORS.cardAlt,
            color: COLORS.text.tertiary,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          {isLoadingMore ? 'Loading...' : 'Load older'}
        </button>
      )}
    </div>
  );
}

function ProvinceEventRow({ event }: { event: ProvinceEventDto }) {
  const { color, glyph } = SEVERITY_STYLE[event.severity];
  return (
    <div
      style={{
        padding: '10px 16px',
        backgroundColor: COLORS.card,
        borderLeft: `2px solid ${color}`,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 2 }}>
        <span aria-hidden="true" style={{ color }}>
          {glyph}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.link,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {formatEnumLabel(event.category)}
        </span>
        <span style={{ fontSize: 11, color: COLORS.text.tertiary }}>{formatGameDate(event.tick)}</span>
      </div>
      <div style={{ fontSize: 13, color: COLORS.text.primary }}>{event.summary}</div>
    </div>
  );
}
